import { create } from 'zustand';
import {
  DEFAULT_AUDIO_CHANNEL,
  DEFAULT_PROGRAM_AUDIO,
  DEFAULT_TRANSITION,
  createLayer,
  type Layer,
  type LayerTree,
  type LayerType,
  type ProgramAudio,
  type Scene,
  type SourceManifestEntry,
  type StudioMode,
  type StudioState,
  type Transition,
} from '@liveforge/shared';

/**
 * Everything the studio is: its scenes, which one is on air, and what the
 * broadcast currently looks like.
 *
 * The Layout Page - in a guest's browser and inside Egress - is a pure
 * renderer of the programme this holds. Keeping ownership in one place is what
 * lets the relay be stateless and the composition be reproduced anywhere from
 * a JSON blob.
 *
 * `revision` increments on every change. It rides along on the wire so a
 * receiver can discard a message that arrives after a newer one.
 *
 * **The two modes differ in one thing: when an edit reaches the air.**
 *
 * In *studio* mode the programme is a snapshot, copied when the director takes
 * a scene live. Editing anything afterwards - including the scene on air -
 * changes nothing being broadcast until the next transition, so no stray drag
 * can reach the audience.
 *
 * In *quick* mode there is no preview to stage in, so the programme simply
 * follows the active scene: what you edit is what goes out. Anything else
 * would mean moving a layer and watching nothing happen.
 *
 * The AUTO layer escapes both. It names nobody and is resolved by each
 * renderer at draw time, so somebody joining appears on air with no
 * transition, in either mode.
 */

function emptyScene(name: string): Scene {
  return { id: crypto.randomUUID(), name, layers: [] };
}

interface StudioStore {
  mode: StudioMode;
  scenes: Scene[];
  /** The scene the programme was taken from. */
  activeSceneId: string | null;
  /** The scene being edited, shown in the preview panel. */
  previewSceneId: string | null;
  transition: Transition;
  /** Frozen at the last go-live. Only consulted in studio mode. */
  snapshot: LayerTree;
  /**
   * The programme mix. It lives here rather than in its own store because it
   * is published in the same message under the same revision - splitting them
   * would let a receiver hold a new layer tree and a stale mix.
   */
  audio: ProgramAudio;
  selectedId: string | null;
  revision: number;
  /**
   * What a published clip should be called, keyed by its LiveKit track name.
   *
   * A clip travels as `media-1`, which tells the track picker nothing - it
   * would otherwise offer "Fabrizio - media-1" and leave the director guessing
   * which trailer that is. The name only exists in the player that reports it,
   * so it is put here for everything else to read.
   */
  mediaLabels: Record<string, string>;
  /**
   * The Live whose stored scenes are currently in this store, or null.
   *
   * This store is a module singleton and outlives any one studio: opening a
   * second Live in the same tab finds it holding the first one's scenes. The
   * save gate therefore compares against the Live being edited rather than
   * asking a boolean whether anything was ever loaded - otherwise the first
   * debounce after navigating would write A's scenes onto B.
   */
  hydratedFor: string | null;
  /**
   * Sources that are publishing right now, described as they will be
   * remembered. Reported by the sources dock, which owns the live objects -
   * a MediaStreamTrack cannot live in a store that gets serialised.
   */
  attachedSources: SourceManifestEntry[];
  /**
   * Sources restored from the last session that have nothing behind them yet.
   *
   * A file's bytes and a captured tab do not survive a reload, but the
   * director's arrangement does: the source keeps its name and its track
   * index, so every layer placing it stays pointed at the right feed and draws
   * the renderer's placeholder until it is given a picture again.
   */
  pendingSources: SourceManifestEntry[];

  select: (id: string | null) => void;
  setMode: (mode: StudioMode) => void;
  setMediaLabel: (trackName: string, label: string) => void;

  /**
   * Replaces the scenes with the ones stored for a Live, and marks the store
   * as belonging to it. Nothing is put on air: see the note on the action.
   */
  load: (liveId: string, scenes: Scene[], studio: StudioState | null) => void;
  /** Closes the save gate, for when the store no longer matches any Live. */
  unhydrate: () => void;

  setAttachedSources: (entries: SourceManifestEntry[]) => void;
  /** A pending source has been given a live one again, under the same index. */
  resolvePending: (id: string) => void;
  /** The director gave up on a restored source and removed the row. */
  dropPending: (id: string) => void;

  addScene: () => void;
  duplicateScene: (id: string) => void;
  renameScene: (id: string, name: string) => void;
  removeScene: (id: string) => void;
  /** Previews a scene in studio mode; takes it to air at once in quick mode. */
  chooseScene: (id: string) => void;
  /** Copies the previewed scene into the programme. Studio mode only. */
  goLive: () => void;
  setTransition: (transition: Transition) => void;

  /** `init` pre-fills the new layer, for adding a source straight to a scene. */
  addLayer: (type: LayerType, init?: Partial<Layer>) => void;
  removeLayer: (id: string) => void;
  updateLayer: (id: string, patch: Partial<Layer>) => void;
  /** Moves a layer to a new position in the top-first panel ordering. */
  reorder: (id: string, toIndex: number) => void;
  reset: (layers: Layer[]) => void;

  /**
   * Takes an audio channel on or off air. This touches nobody's device: it
   * decides what the broadcast hears, and it can always be taken back - unlike
   * muting someone's microphone through LiveKit, which they see and which
   * LiveKit refuses to undo remotely.
   */
  setChannelMuted: (channelId: string, muted: boolean) => void;
  setMasterMuted: (muted: boolean) => void;
}

/**
 * zIndex is kept dense and gap-free: the array order *is* the stacking order,
 * so a reorder cannot leave two layers claiming the same depth and the
 * renderer's sort is stable whatever the host does.
 */
function renumber(layers: Layer[]): Layer[] {
  return layers.map((layer, index) => ({ ...layer, zIndex: index }));
}

/** Which scene the layer panels are pointed at, which is mode-dependent. */
function editingId(state: StudioStore): string | null {
  return state.mode === 'quick' ? state.activeSceneId : state.previewSceneId;
}

/** Applies a change to the scene being edited, leaving every other one alone. */
function editScene(state: StudioStore, change: (layers: Layer[]) => Layer[]) {
  const id = editingId(state);
  if (!id) {
    return {};
  }
  return {
    scenes: state.scenes.map((scene) =>
      scene.id === id ? { ...scene, layers: change(scene.layers) } : scene,
    ),
    revision: state.revision + 1,
  };
}

/**
 * The dock rebuilds its manifest on every render of a list that changes for
 * reasons the manifest does not care about - a playhead tick, a track being
 * re-attached. Comparing by value keeps those out of the store, and so out of
 * the debounced save.
 */
function sameManifest(a: SourceManifestEntry[], b: SourceManifestEntry[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((entry, i) => {
    const other = b[i];
    return (
      other !== undefined &&
      entry.id === other.id &&
      entry.index === other.index &&
      entry.kind === other.kind &&
      entry.name === other.name &&
      entry.videoDeviceId === other.videoDeviceId &&
      entry.audioDeviceId === other.audioDeviceId &&
      entry.fileName === other.fileName &&
      entry.videoId === other.videoId
    );
  });
}

const FIRST_SCENE = emptyScene('Scene 1');

export const useStudioStore = create<StudioStore>((set) => ({
  mode: 'studio',
  scenes: [FIRST_SCENE],
  activeSceneId: FIRST_SCENE.id,
  previewSceneId: FIRST_SCENE.id,
  transition: DEFAULT_TRANSITION,
  snapshot: [],
  audio: DEFAULT_PROGRAM_AUDIO,
  selectedId: null,
  revision: 0,
  mediaLabels: {},
  hydratedFor: null,
  attachedSources: [],
  pendingSources: [],

  select: (id) => set({ selectedId: id }),

  setMediaLabel: (trackName, label) =>
    set((state) =>
      // No revision bump: this is how the studio words a track, not part of
      // the composition, and it must not go out on the wire as a change.
      state.mediaLabels[trackName] === label
        ? state
        : { mediaLabels: { ...state.mediaLabels, [trackName]: label } },
    ),

  setMode: (mode) =>
    set((state) => {
      if (mode === state.mode) {
        return state;
      }
      // Leaving quick mode freezes whatever is currently going out, so the
      // switch itself never changes the picture. Entering it hands control of
      // the air back to the active scene, which is the point of the mode.
      const active = state.scenes.find((scene) => scene.id === state.activeSceneId);
      return {
        mode,
        snapshot: mode === 'studio' ? (active?.layers ?? []) : state.snapshot,
        previewSceneId: state.previewSceneId ?? state.activeSceneId,
        revision: state.revision + 1,
      };
    }),

  load: (liveId, scenes, studio) =>
    set((state) => {
      // A Show with no preset yet, or a Live saved when the last scene was
      // deleted, must still leave the director something to edit.
      const list =
        scenes.length > 0 ? scenes.map((scene) => ({ ...scene })) : [emptyScene('Scene 1')];
      const first = list[0]?.id ?? null;
      const has = (id: string | null | undefined) =>
        id && list.some((scene) => scene.id === id) ? id : null;

      // A stored id can name a scene that no longer exists - deleted in
      // another tab, or the Live's override replaced wholesale - so every one
      // of them is checked against the list rather than trusted.
      const active = has(studio?.activeSceneId) ?? first;
      const preview = has(studio?.previewSceneId) ?? active;

      // The programme comes back. This is the point of storing the state at
      // all: the room, the guests and the relay all survived the reload, and a
      // host that returned publishing an empty tree would be the only thing
      // that blacked out the broadcast. It is safe because the scene is the
      // one that was on air, not an arbitrary one - copied, as goLive() does,
      // so later edits cannot leak into what is being sent.
      const onAir = list.find((scene) => scene.id === active);

      return {
        scenes: list,
        mode: studio?.mode ?? 'studio',
        activeSceneId: active,
        previewSceneId: preview,
        transition: studio?.transition ?? DEFAULT_TRANSITION,
        snapshot: onAir ? onAir.layers.map((layer) => ({ ...layer })) : [],
        // Nothing is attached yet: the dock reports what it manages to bring
        // back, and everything stored starts out waiting for it.
        attachedSources: [],
        pendingSources: studio?.sources ?? [],
        selectedId: null,
        hydratedFor: liveId,
        revision: state.revision + 1,
      };
    }),

  unhydrate: () => set({ hydratedFor: null }),

  // None of these bump `revision`. Which sources exist is studio bookkeeping,
  // not part of the composition on the wire - a layer names a track, and the
  // renderer resolves it or draws a placeholder.
  setAttachedSources: (entries) =>
    set((state) =>
      sameManifest(state.attachedSources, entries) ? state : { attachedSources: entries },
    ),

  resolvePending: (id) =>
    set((state) => ({ pendingSources: state.pendingSources.filter((entry) => entry.id !== id) })),

  dropPending: (id) =>
    set((state) => ({ pendingSources: state.pendingSources.filter((entry) => entry.id !== id) })),

  addScene: () =>
    set((state) => {
      const scene = emptyScene(`Scene ${state.scenes.length + 1}`);
      return {
        scenes: [...state.scenes, scene],
        // Selected for editing, never taken to air: creating a scene must not
        // put an empty frame in front of the audience.
        previewSceneId: state.mode === 'studio' ? scene.id : state.previewSceneId,
        activeSceneId: state.activeSceneId ?? scene.id,
        selectedId: null,
        revision: state.revision + 1,
      };
    }),

  duplicateScene: (id) =>
    set((state) => {
      const source = state.scenes.find((scene) => scene.id === id);
      if (!source) {
        return state;
      }
      // Fresh layer ids: two scenes sharing them would make selecting a layer
      // in one highlight a different layer in the other.
      const copy: Scene = {
        id: crypto.randomUUID(),
        name: `${source.name} copy`,
        layers: source.layers.map((layer) => ({ ...layer, id: crypto.randomUUID() })),
      };
      const at = state.scenes.findIndex((scene) => scene.id === id) + 1;
      const scenes = [...state.scenes];
      scenes.splice(at, 0, copy);
      return {
        scenes,
        previewSceneId: state.mode === 'studio' ? copy.id : state.previewSceneId,
        revision: state.revision + 1,
      };
    }),

  renameScene: (id, name) =>
    set((state) => ({
      scenes: state.scenes.map((scene) => (scene.id === id ? { ...scene, name } : scene)),
      revision: state.revision + 1,
    })),

  removeScene: (id) =>
    set((state) => {
      // The last scene is never removed: a studio with no scenes has nothing
      // to edit and no way back.
      if (state.scenes.length <= 1) {
        return state;
      }
      const scenes = state.scenes.filter((scene) => scene.id !== id);
      const fallback = scenes[0]?.id ?? null;
      return {
        scenes,
        activeSceneId: state.activeSceneId === id ? fallback : state.activeSceneId,
        previewSceneId: state.previewSceneId === id ? fallback : state.previewSceneId,
        selectedId: null,
        revision: state.revision + 1,
      };
    }),

  chooseScene: (id) =>
    set((state) => {
      if (state.mode === 'quick') {
        // Click is air. There is no staging step in this mode, which is the
        // whole reason it exists.
        return {
          activeSceneId: id,
          previewSceneId: id,
          selectedId: null,
          revision: state.revision + 1,
        };
      }
      return { previewSceneId: id, selectedId: null, revision: state.revision + 1 };
    }),

  goLive: () =>
    set((state) => {
      const preview = state.scenes.find((scene) => scene.id === state.previewSceneId);
      if (!preview) {
        return state;
      }
      return {
        activeSceneId: preview.id,
        // Preview and programme swap, as every vision mixer does: what just
        // left the air lands back in preview, which is where a director
        // usually wants it - either to cut back, or to fix what went wrong
        // with it. Re-taking the same scene leaves this alone.
        previewSceneId: state.activeSceneId ?? preview.id,
        // Copied, not referenced: later edits to this scene must not leak into
        // what is being broadcast.
        snapshot: preview.layers.map((layer) => ({ ...layer })),
        selectedId: null,
        revision: state.revision + 1,
      };
    }),

  setTransition: (transition) => set({ transition }),

  addLayer: (type, init) =>
    set((state) => {
      const id = editingId(state);
      const scene = state.scenes.find((entry) => entry.id === id);
      if (!scene) {
        return state;
      }
      const created = createLayer(type, crypto.randomUUID(), scene.layers.length);
      const layer = (init ? { ...created, ...init } : created) as Layer;
      return {
        ...editScene(state, (layers) => [...layers, layer]),
        // Selected immediately: it lands in the middle of the frame and the
        // host almost always wants to move or fill it next.
        selectedId: layer.id,
      };
    }),

  removeLayer: (id) =>
    set((state) => ({
      ...editScene(state, (layers) => renumber(layers.filter((layer) => layer.id !== id))),
      selectedId: state.selectedId === id ? null : state.selectedId,
    })),

  updateLayer: (id, patch) =>
    set((state) =>
      editScene(state, (layers) =>
        layers.map((layer) =>
          // The cast is confined to this one line. A patch is only ever built
          // by the properties panel for the layer it is editing, so its fields
          // belong to that layer's own variant - which the union cannot
          // express without threading the discriminant through every caller.
          layer.id === id ? ({ ...layer, ...patch } as Layer) : layer,
        ),
      ),
    ),

  reorder: (id, toIndex) =>
    set((state) =>
      editScene(state, (layers) => {
        // The panel lists top-first, the array is bottom-first, so an index
        // from the panel has to be flipped before it means anything here.
        const ordered = [...layers];
        const from = ordered.findIndex((layer) => layer.id === id);
        if (from === -1) {
          return layers;
        }
        const target = Math.min(Math.max(ordered.length - 1 - toIndex, 0), ordered.length - 1);
        const [moved] = ordered.splice(from, 1);
        if (!moved) {
          return layers;
        }
        ordered.splice(target, 0, moved);
        return renumber(ordered);
      }),
    ),

  reset: (layers) => set((state) => editScene(state, () => renumber(layers))),

  setChannelMuted: (channelId, muted) =>
    set((state) => ({
      audio: {
        ...state.audio,
        channels: {
          ...state.audio.channels,
          [channelId]: { ...(state.audio.channels[channelId] ?? DEFAULT_AUDIO_CHANNEL), muted },
        },
      },
      revision: state.revision + 1,
    })),

  setMasterMuted: (muted) =>
    set((state) => ({
      audio: { ...state.audio, master: { ...state.audio.master, muted } },
      revision: state.revision + 1,
    })),
}));

/** The scene the layer panel and properties panel are pointed at. */
export function editingScene(state: StudioStore): Scene | undefined {
  const id = editingId(state);
  return state.scenes.find((scene) => scene.id === id);
}

/**
 * What is actually being broadcast.
 *
 * In quick mode the programme follows the active scene, so an edit is on air
 * as soon as it is made. In studio mode it is the snapshot taken at the last
 * go-live, and nothing but another go-live can change it.
 */
export function programLayers(state: StudioStore): LayerTree {
  if (state.mode === 'quick') {
    return state.scenes.find((scene) => scene.id === state.activeSceneId)?.layers ?? [];
  }
  return state.snapshot;
}

/**
 * What gets stored: everything publishing, plus everything still waiting to be
 * reattached. A restored source the director has not fixed yet must survive
 * the next save, or closing the tab twice would quietly discard it.
 */
export function sourceManifest(state: StudioStore): SourceManifestEntry[] {
  return [...state.attachedSources, ...state.pendingSources].sort((a, b) => a.index - b.index);
}

/** What the studio was doing, in the shape the Live stores it. */
export function studioState(state: StudioStore): StudioState {
  return {
    mode: state.mode,
    activeSceneId: state.activeSceneId,
    previewSceneId: state.previewSceneId,
    transition: state.transition,
    sources: sourceManifest(state),
  };
}

/** The panel's ordering: topmost layer first, as every design tool shows it. */
export function topFirst(layers: Layer[]): Layer[] {
  return [...layers].sort((a, b) => b.zIndex - a.zIndex);
}
